import type { AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import getBlogPageData from "./getBlogPageData";
import { fetchRedirects, transformRedirectsToMap } from "./helper";
import type { RegionalBlogLanguage } from "@Pages/Blog/utils/getBlogCollectionUrl";

export type Image = {
  id: number;
  alt: string;
  prefix?: string;
  updatedAt: string;
  createdAt: string;
  url: string;
  thumbnailURL?: string | null;
  filename?: string;
  mimeType?: string;
  filesize?: number;
  width?: number;
  height?: number;
  focalX?: number;
  focalY?: number;
};

export type Category = {
  id: number;
  name: string;
  slug: string;
  metaDescription?: string;
  webflowId?: string;
  title?: string;
  categoriesTagline?: string;
  thumbnail?: Image;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type Tag = {
  id: number;
  name: string;
  slug: string;
  title?: string;
  metaDescription?: string;
  webflowId?: string;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type CountryTag = {
  id: number;
  name: string;
  slug: string;
  countryCode?: string | null;
  webflowId?: string;
  rankning?: number | null;
  logo?: Image;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type PostBodyNode = {
  type: string;
  format?: string;
  indent?: number;
  version?: number;
  children?: any[];
  direction?: string | null;
};

export type PostBody = {
  root: PostBodyNode;
};

export type FAQItem = {
  id: string;
  question: string;
  answerRichText: Record<string, any>;
};

export type BlogPost = {
  id: number;
  name: string;
  slug: string;
  mainImage: Image;
  category?: Category;
  tags?: Tag[];
  countryTag?: CountryTag;
  date?: string;
  webflowlastPublished?: string;
  readTime?: string;
  taglines?: string;
  postBody?: PostBody;
  tocBasedOn?: string;
  featured?: boolean;
  video?: string | null;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  isFaqSchema?: "yes" | "no" | null;
  faqItems?: FAQItem[];
  isVideoObjectSchema?: "yes" | "no" | null;
  videoThumbnailUrl?: string | null;
  videoContentUrl?: string | null;
  videoEmbedUrl?: string | null;
  videoPublishedDate?: string | null;
  videoDuration?: string | null;
  isHowToSchema?: "yes" | "no" | null;
  scriptForHowToSchema?: string | null;
  isItemListSchema?: "yes" | "no" | null;
  scriptForItemListSchema?: string | null;
  webflowId?: string;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type BlogPostSimple = {
  id: number;
  name: string;
  slug: string;
  mainImage: Image;
  category: Category;
  date: string;
  updatedAt?: string;
  readTime: string;
};

export type BlogPartner = {
  id: string;
  image: Image;
  link: string;
};

export type BlogPartnersData = {
  id: number;
  partners: BlogPartner[];
  updatedAt: string;
  createdAt: string;
};

interface BlogPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  slug: string;
  data: BlogPost | null;
  categoryData: any;
  sidebarFeaturedBlogs: BlogPostSimple[];
  blogPartners: BlogPartner[];
  categories: Category[];
  error: any;
  canonical_url: string | null;
  isCategory: boolean;
  isTag: boolean;
  isSearch: boolean;
  searchQuery: string;
  isLatest: boolean;
  totalDocs: number;
  redirects: Record<string, string>; // Map of "to" -> "from" paths
}

export const initialState: BlogPageData = {
  readyStatus: "invalid",
  slug: "",
  data: null,
  categoryData: null,
  sidebarFeaturedBlogs: [],
  blogPartners: [],
  categories: [],
  error: null,
  canonical_url: null,
  isCategory: false,
  isTag: false,
  isSearch: false,
  searchQuery: "",
  isLatest: false,
  totalDocs: 0,
  redirects: {},
};

const blogPageSlice = createSlice({
  name: "blogPageData",
  initialState,
  reducers: {
    updateBlogData: (state: BlogPageData, { payload }: PayloadAction<any>) => {
      state.data = payload?.data || null;
      state.categoryData = payload?.categoryData || null;
      state.sidebarFeaturedBlogs = payload?.sidebarFeaturedBlogs || [];
      state.blogPartners = payload?.blogPartners || [];
      state.categories = payload?.categories || [];
      state.slug = payload.slug;
      state.canonical_url = payload.canonical_url;
      state.isCategory = payload.isCategory || false;
      state.isTag = payload.isTag || false;
      state.isSearch = payload.isSearch || false;
      state.searchQuery = payload.searchQuery || "";
      state.isLatest = payload.isLatest || false;
      state.totalDocs = payload.totalDocs || 0;
    },
    updateBlogReadyStatus: (
      state: BlogPageData,
      { payload }: PayloadAction<"invalid" | "request" | "success" | "failure">,
    ) => {
      state.readyStatus = payload;
    },
    resetBlogData: (state: BlogPageData) => {
      state.readyStatus = "invalid";
      state.data = null;
      state.sidebarFeaturedBlogs = [];
      state.blogPartners = [];
      state.categories = [];
      state.error = null;
      state.isCategory = false;
      state.isTag = false;
      state.isSearch = false;
      state.searchQuery = "";
      state.isLatest = false;
      state.totalDocs = 0;
    },
    updateBlogRedirects: (state: BlogPageData, { payload }: PayloadAction<any>) => {
      state.redirects = payload;
    },
  },
});

export const { updateBlogData, updateBlogReadyStatus, resetBlogData, updateBlogRedirects } =
  blogPageSlice.actions;

export default blogPageSlice.reducer;

// Selector to check if we should fetch
export const shouldFetchBlog = (blogPageDataState: BlogPageData, slug: string) =>
  blogPageDataState.readyStatus === "invalid" || blogPageDataState.slug !== slug;

// Main thunk for fetching if needed
export const fetchBlogPageDataIfNeeded =
  (
    props: any,
    isCategory = false,
    isTag = false,
    isSearch = false,
    searchQuery = "",
    isLatest = false,
    isDraft = false,
    lang?: RegionalBlogLanguage,
  ): AppThunk =>
  async (dispatch, getState: any) => {
    if (props?.staticContext?.statusCode == 301) {
      return;
    }
    // Extract slug from params (works for both client and server)
    // For latest route, there is no slug param, so we use "latest" as identifier
    let slug: string;
    if (isLatest) {
      slug = "latest";
    } else {
      slug = __CLIENT__ ? props?.match?.params?.slug : props?.params?.slug;
    }
    const href = (__CLIENT__ ? window.location.href : props.req?.url) || null;

    const state = getState();

    // For search, use searchQuery as the identifier instead of slug
    // For latest, use "latest" as the identifier
    let identifier: string;
    if (isLatest) {
      identifier = "latest";
    } else if (isSearch) {
      identifier = searchQuery;
    } else {
      identifier = slug;
    }

    // For search queries, always refetch if the query has changed
    const shouldRefetch = isSearch
      ? state.blogPageData.searchQuery !== searchQuery ||
        shouldFetchBlog(state.blogPageData, identifier)
      : shouldFetchBlog(state.blogPageData, identifier);

    // Only fetch if we don't have the data or it's a different slug/query
    if (shouldRefetch) {
      await dispatch(
        getBlogPageData({
          slug: identifier,
          url: href,
          staticContext: props.staticContext,
          isCategory,
          isTag,
          isSearch,
          searchQuery,
          isLatest,
          isDraft,
          lang,
        }),
      );
      return;
    }

    // Scroll to top on client-side navigation
    if (state.router.action === "PUSH" && __CLIENT__) {
      window.scrollTo(0, 0);
    }

    // eslint-disable-next-line consistent-return
    return null;
  };

export const blogMiddlewareServer =
  (props: any): AppThunk =>
  async (dispatch) => {
    const path = __SERVER__ ? props?.req?.path : window.location.pathname;

    // Match both /blog and /:lang/blog paths (where lang is es, de, or fr)
    const blogPathMatch = path.match(/^(?:\/(es|de|fr))?\/blog/);
    const isDraftBlog = path.startsWith("/drafts-blog");

    if (!blogPathMatch && !isDraftBlog) {
      return;
    }

    try {
      const response = await fetchRedirects(0);
      const redirectMap = transformRedirectsToMap(response.docs);
      dispatch(updateBlogRedirects(redirectMap));

      // Strip language prefix and /blog or /drafts-blog prefix for redirect lookup
      const langPrefix = blogPathMatch?.[1] ? `/${blogPathMatch[1]}` : "";
      const blogPath = path
        .replace(langPrefix, "")
        .replace(/^\/blog/, "")
        .replace(/^\/drafts-blog/, "");

      if (redirectMap[blogPath]) {
        // Prepend language prefix to redirect target when language is present
        const redirectTo = `${langPrefix}${redirectMap[blogPath]}`;

        if (__SERVER__ && props.staticContext) {
          props.staticContext.url = redirectTo;
          props.staticContext.statusCode = 301;
        }
      }
    } catch (error) {
      console.error("[Blog Redirects] Error fetching redirects:", error);
    }
  };
